import { Link, usePage } from "@inertiajs/react";
import { ButtonLink } from "@/components/ui/Button";
import hackClubFlag from "@/assets/brand/hack-club-flag.webp";
import logoDetail from "@/assets/brand/logo-detail.png";
import type { PublicProps } from "@/types";

// The Figma puts the Hack Club flag in the nav, not the Boba Bash wordmark -
// the wordmark is already the size of a poster in the hero directly below.
export function LandingNav({
  signedIn,
  // A city page points "sign up!" at its own form; everywhere else it means
  // "pick a city first".
  signUpHref = "/events",
}: {
  signedIn: boolean;
  signUpHref?: string;
}) {
  // Only once hand-ins are actually open. Before then it is a link to a page
  // that can only tell them to come back later.
  const program = usePage<PublicProps>().props.program;
  const submissionsOpen = program.submissions_open;
  // With the season closed there is nothing to sign up for, only the events
  // that already ran.
  const seasonOver = !program.organizer_boards_open;

  return (
    <header className="border-line bg-surface/95 sticky top-0 z-40 border-b-2 backdrop-blur">
      <nav className="mx-auto flex w-full max-w-6xl items-center gap-4 px-4 py-2 sm:px-6">
        <a
          href="https://hackclub.com"
          target="_blank"
          rel="noreferrer"
          className="min-w-0 shrink-0"
        >
          <img
            src={hackClubFlag}
            alt="Hack Club"
            className="h-7 w-auto sm:h-8"
          />
        </a>

        {/* home, about, FAQ, then "sign up!" carrying the accent. Signing up
            means picking a city, so it points at the event list rather than at
            auth - attendees never make an account. */}
        <div className="font-display text-caption text-ink sm:text-body ml-auto flex items-center gap-4 font-bold sm:gap-6">
          <a href="#top" className="hover:text-link hidden sm:inline">
            home
          </a>
          <a href="#what" className="hover:text-link hidden sm:inline">
            about
          </a>
          <a href="#faq" className="hover:text-link hidden sm:inline">
            FAQ
          </a>
          <Link
            href={seasonOver ? "/events" : signUpHref}
            className="text-sunset-orange hover:underline"
          >
            {seasonOver ? "past events" : "sign up!"}
          </Link>
          {submissionsOpen && (
            <Link href="/submit" className="hover:text-link">
              hand in
            </Link>
          )}
          {signedIn ? (
            <ButtonLink href="/home" variant="brand" size="sm">
              Dashboard
            </ButtonLink>
          ) : (
            <a
              href="/auth/hca"
              className="text-ink-soft hover:text-link hidden font-normal sm:inline"
            >
              organizer log in
            </a>
          )}
        </div>
      </nav>
    </header>
  );
}

export function LandingFooter() {
  const { props } = usePage<PublicProps>();
  const { support_channel_name, support_channel_url, support_email } =
    props.program;

  // The Figma's footer band: pale sky, the small logo, two italic lines, then
  // a "learn more" row of red links.
  return (
    <footer className="border-line bg-band-sky border-t-2 px-4 py-12 sm:px-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-4 text-center">
        <img src={logoDetail} alt="" className="h-20 w-auto" aria-hidden />

        <p className="font-display text-subheading text-ink-muted font-bold italic">
          made with ♡ by teens, for teens
          <span className="block">
            by the {props.program.name} team @ Hack Club
          </span>
        </p>

        <div className="flex flex-col items-center gap-1">
          <span className="text-caption text-ink-soft">learn more:</span>
          <div className="text-caption text-sunset-orange flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-bold">
            <a
              className="underline hover:no-underline"
              href="https://hackclub.com"
            >
              Hack Club
            </a>
            <span aria-hidden className="text-ink-soft">
              ・
            </span>
            {support_channel_name ? (
              <a
                className="underline hover:no-underline"
                href={support_channel_url ?? undefined}
                target="_blank"
                rel="noreferrer"
              >
                #{support_channel_name}
              </a>
            ) : (
              <a
                className="underline hover:no-underline"
                href="https://hackclub.com/slack"
              >
                Slack
              </a>
            )}
            <span aria-hidden className="text-ink-soft">
              ・
            </span>
            <Link className="underline hover:no-underline" href="/events">
              Events
            </Link>
            <span aria-hidden className="text-ink-soft">
              ・
            </span>
            <Link className="underline hover:no-underline" href="/guides">
              Guides
            </Link>
            {support_email && (
              <>
                <span aria-hidden className="text-ink-soft">
                  ・
                </span>
                <a
                  className="underline hover:no-underline"
                  href={`mailto:${support_email}`}
                >
                  {support_email}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
